import React, { useState } from 'react'
import Button from '@components/Button'
import Card from '@components/Card'
import Container from '@components/Container'
import Header from '@components/Header'
import StepDescription from '@components/StepDescription'
import Toggle from '@components/Toggle'
import Show from '@components/Show'
import MainLayout from '@layouts/MainLayout'
import { colors } from '@lib/colors'
import { i18n } from '@lib/i18n'
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native'
import uuid from 'react-native-uuid'
import api from '@lib/api'
import useSentry from '@hooks/useSentry'
import { useSnackbar, SnackbarThemes } from '@context/SnackbarContext'
import { useCurrentUser } from '@context/CurrentUserContext'
import { usePendingWorkOrders } from '@context/PendingWorkOrdersContext'
import useNavigation from '@hooks/useNavigation'

const COPY = i18n.en.WORK_ORDERS.CREATE.ADD_DESCRIPTION

export default function ({ onNext, onPrevious, onUpdate, context }: DefaultStepProps) {
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [description, setDescription] = useState<string>(context.details!.description)

  const [urgent, setUrgent] = useState<boolean>(context.details!.urgent)
  const toggleUrgent = () => setUrgent(!urgent)

  const [afterHours, setAfterHours] = useState<boolean>(context.details!.afterHours)
  const toggleAfterHours = () => setAfterHours(!afterHours)

  const { captureException } = useSentry()
  const { setSnackbarMessage } = useSnackbar()
  const { currentUser } = useCurrentUser()
  const { saveLocalPendingWorkOrder } = usePendingWorkOrders()
  const navigation = useNavigation()

  const handleGoToNextScreen = async () => {
    setSubmitting(true)
    const newWorkOrder = {
      ...context,
      id: uuid.v4() as string,
      details: {
        description,
        urgent,
        afterHours,
      },
    }
    if (currentUser?.email) {
      newWorkOrder.email = currentUser.email
    }

    try {
      await api.createWorkOrder(newWorkOrder)
      saveLocalPendingWorkOrder(newWorkOrder)
      onNext({
        description,
        urgent,
        afterHours,
      })
    } catch (error) {
      captureException(error)
      setSnackbarMessage('Server error creating work order', SnackbarThemes.error)
      setSubmitting(false)
    }
  }

  const onNavigateBack = () => {
    onUpdate({
      description,
      urgent,
      afterHours,
    })
    onPrevious()
  }

  const handleGoToPreviousScreen = () => {
    onNavigateBack()
    navigation.goBack()
  }

  return (
    <MainLayout
      onNavigateBack={ onNavigateBack }
    >
      <Container>
        <View style={ styles.container }>
          <View style={ styles.contentWrapper }>
            <View>
              <Header>{ COPY.HEADER }</Header>
              <StepDescription>{ COPY.DESCRIPTION }</StepDescription>
              <Card style={ styles.card }>
                <TextInput
                  onChangeText={ setDescription }
                  style={ styles.input }
                  textAlignVertical={ 'top' }
                  multiline
                  numberOfLines={ 9 }
                  placeholder={ COPY.DESCRIPTION_INPUT_PLACEHOLDER }
                  value={ description }
                />

                <Toggle
                  label={ COPY.URGENT_LABEL }
                  toggled={ urgent }
                  onValueChange={ toggleUrgent }
                />
                <Toggle
                  label={ COPY.AFTER_HOURS_LABEL }
                  toggled={ afterHours }
                  onValueChange={ toggleAfterHours }
                />
              </Card>

              <Show when={urgent && false}>
                {/* Disabled for now */}
                <Text style={ styles.urgentNotice }>{ COPY.URGENT_NOTICE }</Text>
              </Show>
            </View>

            <Show when={submitting}>
              <ActivityIndicator />
            </Show>

            <View style={ styles.bottomButtons }>
              <Button
                label={ COPY.PREV_STEP }
                buttonStyles={ styles.buttonPrev }
                onPress={ handleGoToPreviousScreen }
              />
              <Button
                label={ COPY.NEXT_STEP }
                disabled={ !description || submitting }
                buttonStyles={ styles.buttonNext }
                disabledButtonStyles={ styles.buttonNextDisabled }
                onPress={ handleGoToNextScreen }
              />
            </View>
          </View>
        </View>
      </Container>
    </MainLayout>
  )
}

const styles = StyleSheet.create({
  bottomButtons: {
    flexDirection: 'row',
  },
  buttonPrev: {
    backgroundColor: colors.gray[100],
    flex: 1,
    marginRight: 10,
  },
  buttonNextDisabled: {
    backgroundColor: colors.gray[100],
    flex: 1,
    marginLeft: 10,
  },
  buttonNext: {
    backgroundColor: colors.green[500],
    flex: 1,
    marginLeft: 10,
  },
  card: {
    marginBottom: 20,
    marginTop: 20,
  },
  container: {
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
    justifyContent: 'space-between',
  },
  input: {
    backgroundColor: colors.gray[50],
    borderRadius: 6,
    height: 110,
    padding: 10,
  },
  urgentNotice: {
    color: colors.red[400],
    textAlign: 'center',
  },
})
